import React, { useState, useMemo, useEffect, useRef } from 'react'
import {
  CheckboxV2,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  menuItemRowInteractiveClasses,
  menuItemLabelClasses,
  menuItemEmphasisLabelClasses
} from '@shopflo/ui'
import { classNames } from 'src/lib/utils/helpers'
import { useLocale } from 'src/lib/hooks/useLocal'
import { CaretDown, MagnifyingGlass } from '@phosphor-icons/react'

/** Matches Orders filter checkbox list: `[data-multiselect-option-row]` / `.multiselect-options-scroll`. */
const MULTISELECT_OPTIONS_SCROLL = 'multiselect-options-scroll'

const OPTION_ROW_BTN =
  `${menuItemRowInteractiveClasses} focus:outline-none focus-visible:outline-none focus-visible:ring-0 first:bg-zinc-50 [.multiselect-options-scroll:has([data-multiselect-option-row]:focus-visible)_&]:not(:focus-visible):hover:bg-transparent`

const OPTION_ROW_LABEL_SELECT_ALL =
  `${menuItemEmphasisLabelClasses} [.multiselect-options-scroll:has([data-multiselect-option-row]:focus-visible)_button:not(:focus-visible):hover_&]:text-zinc-700`

const OPTION_ROW_LABEL =
  `${menuItemLabelClasses} [.multiselect-options-scroll:has([data-multiselect-option-row]:focus-visible)_button:not(:focus-visible):hover_&]:text-zinc-600`

export interface MultiSelectItem {
  /** Unique key/id for the item */
  key: string
  /** Display value/label for the item */
  value: string
  /** Optional description shown on the right side (e.g., "2103 Pincodes") */
  description?: string
  /** Optional disabled state */
  disabled?: boolean
}

interface GenericMultiSelectDialogProps {
  /** Array of items to display */
  items: MultiSelectItem[]
  /** Array of selected item keys */
  selectedKeys: string[]
  /** Callback when selection changes */
  onSelectionChange: (selectedKeys: string[]) => void
  /** Placeholder text when no items are selected */
  placeholder?: string
  /** Placeholder component when no items are selected */
  placeholderComponent?: React.ReactNode
  /** Placeholder text for search input */
  searchPlaceholder?: string
  /** Whether to show search input */
  showSearch?: boolean
  /** Text for the add/confirm button */
  addButtonText?: string
  /** Callback when add button is clicked */
  onAdd?: (selectedKeys: string[]) => void
  /** Whether to show the add button */
  showAddButton?: boolean
  /** Empty state title */
  emptyStateTitle?: string
  /** Empty state description */
  emptyStateDescription?: string
  /** Custom class name for trigger */
  triggerClassName?: string
  /** Custom class name for dropdown content */
  contentClassName?: string
  /** Popover/dropdown alignment relative to the trigger */
  align?: 'start' | 'center' | 'end'
  /** Optional callback when modal closes */
  onClose?: () => void
  /** Whether the modal is open (for controlled mode) */
  isOpen?: boolean
  /** When true, at least one value must remain selected; the last selected checkbox is disabled */
  requireAtLeastOne?: boolean
  /** Use Popover instead of DropdownMenu (e.g. state pickers in sheets). */
  surface?: 'dropdown' | 'popover'
}

export const GenericMultiSelectDialog: React.FC<GenericMultiSelectDialogProps> = ({
  items,
  selectedKeys,
  onSelectionChange,
  placeholder,
  placeholderComponent,
  searchPlaceholder = 'Search...',
  showSearch = true,
  addButtonText = 'Add',
  onAdd,
  showAddButton = true,
  emptyStateTitle,
  emptyStateDescription,
  triggerClassName = '',
  contentClassName = '',
  align = 'start',
  onClose,
  isOpen: controlledIsOpen,
  requireAtLeastOne = false,
  surface = 'dropdown'
}) => {
  const { t } = useLocale()
  const [searchTerm, setSearchTerm] = useState('')
  const [internalIsOpen, setInternalIsOpen] = useState(false)

  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen
  const setIsOpen = (open: boolean) => {
    if (controlledIsOpen !== undefined) {
      if (!open) onClose?.()
      return
    }
    setInternalIsOpen(open)
  }

  const handleAdd = () => {
    onAdd?.(selectedKeys)
    setIsOpen(false)
    setSearchTerm('')
    onClose?.()
  }

  const handleAddRef = useRef(handleAdd)
  handleAddRef.current = handleAdd

  // External sync: document keydown for Cmd/Ctrl+Enter while dropdown is open
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault()
        handleAddRef.current()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) return items
    const lowerSearch = searchTerm.toLowerCase()
    return items.filter(
      item =>
        item.value.toLowerCase().includes(lowerSearch) ||
        item.description?.toLowerCase().includes(lowerSearch)
    )
  }, [items, searchTerm])

  const handleItemToggle = (item: MultiSelectItem) => {
    if (item.disabled) return

    const isCurrentlySelected = selectedKeys.includes(item.key)

    if (requireAtLeastOne && isCurrentlySelected && selectedKeys.length === 1) {
      return
    }

    const newSelectedKeys = isCurrentlySelected
      ? selectedKeys.filter(k => k !== item.key)
      : [...selectedKeys, item.key]

    onSelectionChange(newSelectedKeys)
  }

  const selectableFilteredItems = filteredItems.filter(item => !item.disabled)
  const selectedFilteredCount = selectableFilteredItems.filter(item => selectedKeys.includes(item.key)).length
  const allFilteredSelected =
    selectableFilteredItems.length > 0 && selectedFilteredCount === selectableFilteredItems.length
  const someFilteredSelected = selectedFilteredCount > 0 && !allFilteredSelected

  const handleToggleSelectAll = () => {
    const filteredSelectableKeys = selectableFilteredItems.map(item => item.key)
    if (allFilteredSelected) {
      const remaining = selectedKeys.filter(key => !filteredSelectableKeys.includes(key))
      if (requireAtLeastOne && remaining.length === 0) {
        const keepKey = filteredSelectableKeys[0]
        onSelectionChange(keepKey ? [keepKey] : [])
        return
      }
      onSelectionChange(remaining)
      return
    }
    const merged = [...selectedKeys]
    filteredSelectableKeys.forEach(key => {
      if (!merged.includes(key)) merged.push(key)
    })
    onSelectionChange(merged)
  }

  const handleOpenChange = (open: boolean) => {
    if (controlledIsOpen === undefined) {
      setInternalIsOpen(open)
    }
    if (!open) {
      setSearchTerm('')
      onClose?.()
    }
  }

  const getDisplayText = () => {
    if (selectedKeys.length === 0) {
      return placeholder || t('select_items')
    }
    if (selectedKeys.length === 1) {
      const item = items.find(i => i.key === selectedKeys[0])
      return item?.value || placeholder || t('select_items')
    }
    return `${selectedKeys.length} selected`
  }

  const sharedContentClass = classNames(
    'max-h-[400px] flex flex-col overflow-hidden rounded-xl border-0 bg-white z-[99999] p-0 !shadow-[0_0_0_0.5px_rgba(9,9,11,0.12),0_16px_16px_-6px_rgba(9,9,11,0.12),0_2px_10px_-6px_rgba(9,9,11,0.48)]',
    contentClassName
  )

  const menuBody = (
    <>
      {showSearch && (
        <div className='sticky top-0 z-10 w-full shrink-0 rounded-t-xl border-b bg-zinc-50'>
          <div className='bg-zinc-50 px-1'>
            <Input
              type='text'
              variant='none'
              dimension='md'
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onMouseDown={e => e.stopPropagation()}
              startAdornment={<MagnifyingGlass className='text-zinc-500' size={20} />}
              className='h-[36px] bg-transparent px-2 !justify-start [&>input]:!bg-transparent rounded-none !overflow-hidden'
            />
          </div>
        </div>
      )}

      <div className={`${MULTISELECT_OPTIONS_SCROLL} flex max-h-[400px] min-h-0 w-full flex-1 flex-col overflow-y-auto outline-none`}>
        {filteredItems.length ? (
          <div className='flex flex-col p-1.5'>
            <button
              type='button'
              data-multiselect-option-row
              role='checkbox'
              aria-checked={allFilteredSelected ? true : someFilteredSelected ? 'mixed' : false}
              className={OPTION_ROW_BTN}
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                handleToggleSelectAll()
              }}
            >
              <CheckboxV2
                className='pointer-events-none shrink-0'
                checked={allFilteredSelected}
                indeterminate={someFilteredSelected}
                size='sm'
                tabIndex={-1}
                aria-hidden
              />
              <span className={OPTION_ROW_LABEL_SELECT_ALL}>Select all</span>
            </button>
            {filteredItems.map(item => {
              const isSelected = selectedKeys.includes(item.key)
              const isLastSelectedAndRequired = requireAtLeastOne && isSelected && selectedKeys.length === 1
              const isCheckboxDisabled = item.disabled || isLastSelectedAndRequired

              return (
                <button
                  key={item.key}
                  type='button'
                  data-multiselect-option-row
                  role='checkbox'
                  aria-checked={isSelected}
                  disabled={item.disabled}
                  className={classNames(OPTION_ROW_BTN, item.disabled ? 'opacity-50 cursor-not-allowed' : '')}
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (!isCheckboxDisabled) handleItemToggle(item)
                  }}
                >
                  <CheckboxV2
                    size='sm'
                    checked={isSelected}
                    disabled={isCheckboxDisabled}
                    className='pointer-events-none shrink-0'
                    tabIndex={-1}
                    aria-hidden
                  />
                  <span className={`${OPTION_ROW_LABEL} truncate`}>{item.value}</span>
                  {item.description ? (
                    <span className='tabular-nums text-text-xs-medium text-zinc-500 shrink-0'>{item.description}</span>
                  ) : null}
                </button>
              )
            })}
          </div>
        ) : (
          <div className='p-4 text-center text-text-sm-regular text-zinc-400'>
            {emptyStateTitle || t('no_items_found')}
          </div>
        )}
      </div>

      {showAddButton && (
        <div className='relative px-1.5 pb-1.5'>
          <Button
            variant='outline'
            size='sm'
            onClick={handleAdd}
            className='w-full flex flex-row items-center justify-center gap-2 p-1'
          >
            {addButtonText}
          </Button>
        </div>
      )}
    </>
  )

  const triggerInner = placeholderComponent ? (
    placeholderComponent
  ) : (
    <div className={classNames('cursor-pointer w-fit', triggerClassName)}>
      <div className='flex items-center gap-2 border border-coal-lighter rounded-lg px-2 py-1 hover:border-primary-dark'>
        <span className={classNames('p-1 text-sm', selectedKeys.length > 0 ? 'text-gray-900' : 'text-gray-500')}>
          {getDisplayText()}
        </span>
        <CaretDown size={16} className='text-zinc-400' />
      </div>
    </div>
  )

  if (surface === 'popover') {
    return (
      <Popover modal={false} open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>{triggerInner}</PopoverTrigger>
        <PopoverContent className={sharedContentClass} align={align} sideOffset={2}>
          {menuBody}
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>{triggerInner}</DropdownMenuTrigger>
      <DropdownMenuContent className={sharedContentClass} align={align} sideOffset={2}>
        {menuBody}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default GenericMultiSelectDialog
